import { __, sprintf, _n } from '@wordpress/i18n';
import { ICS_Admin_Page_Settings } from './interface.currency-switcher-admin-page-settings';
import { IDictionary } from '../../../../common/interface.dictionary';
import { Currency_Switcher_Action_Run_Order_Import } from './actions/class.action-run-order-import';

/**
 * Implements the scripts for the Currency Switcher settings page.
 *
 * @since 5.0.4.230626
 */
export class Currency_Switcher_Admin_Page {
	/**
	 * The singleton instance of this class.
	 *
	 * @var Currency_Switcher_Admin_Page
	 */
	protected static _instance: Currency_Switcher_Admin_Page;

	/**
	 * The settings passed to the class.
	 *
	 * @var ICS_Admin_Page_Settings
	 */
	protected settings: ICS_Admin_Page_Settings;

	/**
	 * Constructor.
	 *
	 * @param settings ICS_Admin_Page_Settings
	 */
	public constructor(settings: ICS_Admin_Page_Settings) {
		this.settings = settings;

		// Store the instance of the class, so that it can be fetched
		// later by calling Currency_Switcher_Admin_Page.instance()
		Currency_Switcher_Admin_Page._instance = this;

		this.init_components();

		this.init_event_handlers();

		this.enhance_select_fields();
	}

	/**
	 * Initialises the components that will provide functionalities on the settings page.
	 *
	 * @return void
	 */
	protected init_components(): void {
		const components: IDictionary<object> = {};

		// Initalise the component that will handle the "import order" action
		components['action_run_order_import'] = new Currency_Switcher_Action_Run_Order_Import(this.settings);
	}

	/**
	 * Displays the configuration section related to the selected Exchange Rates
	 * provider.
	 *
	 * @param string provider_id The ID of the selected Provider.
	 * @return void
	 */
	protected show_exchange_rates_provider_section(provider_id: string): void {
		jQuery('.exchange_rate_model_settings').each((index, element) => {
			let anchor_element = jQuery(element);

			/* Hiding and showing of Providers' specific sections requires a bit of a
			* hack. WordPress renders sections without wrapping them in a DIV, or any
			* container of sort. Therefore, to hide a section, it's necessary to
			* identify one element within it, and then hide the other elements belonging
			* to the same section. In this case, the key element is a paragraph added
			* immediately after the section title.
			* Once such key element is found, all we have to do is hiding the previous
			* sibling (section title) and the next sibling (form-table).
			*/
			if(anchor_element.hasClass(provider_id)) {
				anchor_element
					.show()
					.prev().show().end()
					.next().show();
			}
			else {
				anchor_element
					.hide()
					.prev().hide().end()
					.next().hide();
			}
		});
	}

	/**
	 * Enhances the <select> elements on the page, using a library like jQuery.chosen.
	 *
	 * @return void
	 */
	protected enhance_select_fields(): void {
		const $wc_aelia_currency_switcher_form = jQuery('#wc_aelia_currency_switcher_form');

		// Display tabbed interface
		$wc_aelia_currency_switcher_form.find('.tabs').tabs();

		// Use Chosen plugin to replace standard multiselect
		if(jQuery().chosen) {
			// Multiselect for enabled currencies
			$wc_aelia_currency_switcher_form.find('.enabled_currencies').chosen();

			// Multiselect for payment gateways enabled for each currency
			$wc_aelia_currency_switcher_form.find('.currency_payment_gateways').chosen();

			// Multiselect for the countries linked to a currency
			// @since 4.13.0.220104
			$wc_aelia_currency_switcher_form.find('#currency_country_mappings_settings .currency_countries').chosen();
		}
	}

	/**
	 * Handles the events that allow to enable/disable the "set manual prices" for all currencies.
	 *
	 * @return void
	 */
	protected handle_set_all_manual_prices(): void {
		// Add event handler on "Set All to Manual" checkbox
		const $exchange_rates_settings_table = jQuery('#exchange_rates_settings');

		// When the user clicks on set manually -> select all, tick all manual price checkboxes
		$exchange_rates_settings_table.on('click', '.set_manually .select_all', function() {
			$exchange_rates_settings_table
				.find('.exchange_rate_set_manually')
				.prop('checked', true);
		});

		// When the user clicks on set manually -> deselect all, tick all manual price checkboxes
		$exchange_rates_settings_table.on('click', '.set_manually .deselect_all', function() {
			$exchange_rates_settings_table
				.find('.exchange_rate_set_manually')
				.prop('checked', false);
		});
	}

	/**
	 * Handles the events that show the tooltips.
	 *
	 * @return void
	 */
	protected handle_tooltips(): void {
		// Open tooltips on click
		jQuery('#wc_aelia_currency_switcher_form .help-icon')
			.tooltip({
				content: function() {
					return jQuery(this).attr('tooltip');
				},
				items: 'span'
				})
			.off('mouseover')
			.on('click', function(){
					jQuery(this).tooltip('open');
					return false;
				})
			.attr('tooltip', function() {
				const value = jQuery(this).attr('title');
				jQuery(this).attr('title', '');
				return value;
			})
			.css({ cursor: 'pointer' });
	}

	/**
	 * Handles the sorting of the currency elements on the page.
	 *
	 * @return void
	 */
	protected handle_currency_sorting(): void {
		// Allow to sort the elements
		jQuery('#exchange_rates_settings tbody').sortable({
			handle: '.sort',
			items: 'tr',
			cursor: 'move',
			axis: 'y',
			scrollSensitivity:40,
			forcePlaceholderSize: true,
			helper: 'clone',
			opacity: 0.65,
			placeholder: 'wc-metabox-sortable-placeholder',
			start:function(event,ui){
				ui.item.css('background-color','#f6f6f6');
			},
			stop:function(event,ui){
				ui.item.removeAttr('style');
			}
		});
	}

	/**
	 * Handles the events on the currency/country mapping section.
	 *
	 * @return void
	 */
	protected handle_currency_country_mapping(): void {
		// Automatically populate the currency with all European countries
		// @since 4.13.0.220104
		jQuery('#currency_country_mappings_settings').on('click', '.add_eu_countries', (event) => {
			const $trigger = jQuery(event.target);
			const $target = $trigger.parents('.countries').find('select').first();

			$target.val(wc_aelia_cs_admin_params['european_union_countries']);
			$target.trigger('chosen:updated');
		});
	}

	/**
	 * Handles the display of sections specific to an exchange rates provider.
	 *
	 * @return void
	 */
	protected handle_exchange_rates_provider_section(): void {
		// Add event handler on Exchange Provider dropdown, to only display sections related to it
		const selected_exchange_rates_provider: string = (jQuery('#wc_aelia_currency_switcher\\[exchange_rates_provider\\]').val() as string);
		this.show_exchange_rates_provider_section(selected_exchange_rates_provider);

		jQuery('#wc_aelia_currency_switcher\\[exchange_rates_provider\\]').on('change', (event) => {
			this.show_exchange_rates_provider_section(jQuery(event.target).val() as string);
		});
	}

	/**
	 * Initialises the event handlers that will perform the actions required by the class.
	 */
	protected init_event_handlers() {
		this.handle_set_all_manual_prices();
		this.handle_tooltips();
		this.handle_currency_sorting();
		this.handle_currency_country_mapping();
		this.handle_exchange_rates_provider_section();
	}

	/**
	 * Returns the instance of this class.
	 *
	 * @returns Currency_Switcher_Admin_Page
	 */
	public static instance(): Currency_Switcher_Admin_Page {
		// If the class has not been instantiated, throw an exception. The handler must be
		// initialised first, as it needs a couple of settings to get ready.
		if(!Currency_Switcher_Admin_Page._instance) {
			throw new Error('Class "Currency_Switcher_Admin_Page" has not been instantiated. Please call `Currency_Switcher_Admin_Page.init(<settings>)` before calling `Currency_Switcher_Admin_Page.instance()`.');
		}
		return Currency_Switcher_Admin_Page._instance;
	}

	/**
	 * Initialises the class and its event handlers.
	 *
	 * @param settings ICS_Admin_Page_Settings
	 * @return void
	 */
	public static init(settings: ICS_Admin_Page_Settings): Currency_Switcher_Admin_Page {
		// Initialise the Currency_Switcher_Admin_Page handler. We can't call Currency_Switcher_Admin_Page.instance here,
		// as the handler must be initialised with the settings first
		return new Currency_Switcher_Admin_Page(settings);
	}
}